'use client';
import ProductAdminControls from '@c/admin/ProductAdminControls';
import BikeDetails from '@c/dirt-bikes/BikeDetails';
import PriceGuideChart from '@c/dirt-bikes/PriceGuideChart';
import EstimateDirtBikeShippingModal from '@c/modals/EstimateDirtBikeShippingModal';
import ReportItemModal from '@c/modals/ReportItemModal';
import ProductPriceAndShipping from '@c/product-details/ProductPriceAndShipping';
import ProductConditions from '@c/ProductConditions';
import ProductSellerBadge from '@c/ProductSellerBadge';
import { FlagIcon } from '@heroicons/react/20/solid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Breadcrumb, { type BreadcrumbItem } from '@ui/Breadcrumb';
import Button from '@ui/Button';
import ProductCarousel from '@ui/ProductCarousel';
import { viewContent } from '@util/analytics';
import {
  addProductToFavorites,
  getProductsBySellerId,
  getRelatedProducts,
  removeProductFromFavorites,
} from '@util/firestore/products';
import { trackUserInteraction } from '@util/firestore/recommendations';
import { getReviewsByUid } from '@util/firestore/reviews';
import { getPublicUserDoc } from '@util/firestore/users';
import { useAuth } from 'context/AuthContext';
import { useRouter } from 'next/navigation';
import HomeSection from 'features/homepage/components/Home/components/HomeSection';
import {
  injectBikeProductScript,
  injectBreadcrumbScript,
} from 'features/schemaMarkups';
import { HeartIcon } from 'lucide-react';
import { Montserrat } from 'next/font/google';
import type { ProductDocument } from 'models/product';
import { useEffect, useState } from 'react';
import { useChat } from 'context/ChatContext';
import { ResponsiveCarousel } from 'app/sale/[curated-list]/_components/ResponsiveCarousel';
import Carousel from '@ui/Carousel';
import MXProductCard from '@c/cards/MXProductCard';
import ProductMeetTheSeller from '@c/ProductMeetTheSeller';
import ProductViewerModal from '@c/modals/ProductViewerModal';

interface ProductDetailPageProps {
  product: ProductDocument;
  breadcrumbs: BreadcrumbItem[];
  parts: ProductDocument[];
  isPreview?: boolean;
}

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

export default function ProductDetailPage(props: ProductDetailPageProps) {
  const { product, breadcrumbs, parts } = props;
  const { userDoc } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { openChatModalWithRecipientAndProductId } = useChat();
  const [liked, setLiked] = useState(false);
  const [openModal, setOpenModal] = useState<
    'reportItem' | 'estimateShipping' | ''
  >('');
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (typeof window.fbq === 'function') {
        viewContent(product, 'product', userDoc?.uid ?? '');
        clearInterval(intervalId); // Stop polling when fbq is available
      }
    }, 1000); // Check every second

    return () => clearInterval(intervalId); // Clean up interval on unmount
  }, [product, userDoc?.uid]);

  useEffect(() => {
    if (userDoc) {
      const isFavorite = userDoc.favorites.includes(product.id);
      setLiked(isFavorite);
    }
  }, [userDoc, product.id]);

  const { data: seller } = useQuery({
    queryKey: ['publicUser', product.seller_id],
    queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
    enabled: !!product.seller_id,
  });

  const { data: reviews, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['reviews'],
    queryFn: () =>
      getReviewsByUid({
        uid: product.seller_id ?? '',
        reviewsPerPage: 10,
      }),
    enabled: !!product.seller_id,
  });

  const { data: relatedProducts } = useQuery({
    queryKey: ['relatedProducts', product.id],
    queryFn: () => getRelatedProducts(product, 20),
    throwOnError: false,
  });

  const { data: moreProductsFromSeller } = useQuery({
    queryKey: ['moreProductsFromSeller', product.seller_id],
    queryFn: () =>
      getProductsBySellerId(product.seller_id ?? '', 10, product.id),
    enabled: !!product.seller_id,
    throwOnError: false,
  });

  const handleLikeClick = async () => {
    if (!userDoc?.uid) {
      router.push('/login');
      return;
    }

    const isFavorite = userDoc.favorites.includes(product.id);
    if (isFavorite) {
      await removeProductFromFavorites(userDoc.uid, product.id);
    } else {
      await addProductToFavorites(userDoc.uid, product.id);
    }
    await trackUserInteraction({
      uid: userDoc.uid,
      pid: product.id,
      interaction: isFavorite ? 'unfavorite' : 'favorite',
    });

    setLiked(!liked);

    queryClient.invalidateQueries({
      queryKey: ['authUser'],
    });
  };

  return (
    <>
      <script
        id="schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectBikeProductScript(product) ?? '',
        }}
      />
      <script
        id="breadcrumbSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectBreadcrumbScript({ breadcrumbs }) ?? '',
        }}
      />
      <div className="w-full bg-white">
        <div
          className={`w-full py-8 sm:mx-auto sm:max-w-default ${
            props.isPreview ? 'pointer-events-none cursor-not-allowed' : ''
          }`}
        >
          <div className="flex flex-col gap-y-4">
            <div className="w-full">
              <div className="hidden sm:mx-auto sm:flex sm:max-w-content">
                <Breadcrumb onProductDetails items={breadcrumbs} />
              </div>
            </div>

            {(userDoc?.roles?.support || userDoc?.roles?.admin) && (
              <div className="mx-auto flex w-full flex-col items-center justify-center gap-[0.8rem] lg:flex-row">
                <ProductAdminControls product={product} />
              </div>
            )}

            <div className="flex w-full flex-col gap-y-8 sm:w-auto">
              <div
                className={`flex w-full max-w-default flex-col justify-center gap-[3.2rem] sm:gap-[4.8rem] lg:flex-row `}
              >
                <div>
                  <ProductCarousel
                    product={product}
                    openProductViewer={(index: number) => {
                      setCurrentImageIndex(index);
                      setImageViewerOpen(true);
                    }}
                    selectedVariations={null}
                    stack
                  />
                </div>

                <div className="flex w-full max-w-[60rem] flex-col gap-[1.6rem] px-8 sm:gap-[1.6rem] sm:px-4 lg:pt-[.5rem]">
                  <div className="flex flex-col gap-8">
                    <h1 className="text-[2.5rem] font-bold leading-[3.25rem] text-black sm:w-[50rem] sm:text-[2.5rem]">
                      {product.title}
                    </h1>

                    {seller?.uid && product.id && (
                      <ProductSellerBadge
                        sellerDoc={seller}
                        product={product}
                        reviews={reviews ?? []}
                        isLoading={isLoadingReviews}
                      />
                    )}
                  </div>

                  <div className="mt-8 flex flex-col items-start gap-[1.6rem] xl:flex-row">
                    <ProductPriceAndShipping product={product} />
                  </div>

                  <div className="mt-6 flex flex-col gap-4">
                    <ProductConditions product={product} />

                    <div className="flex flex-col gap-2">
                      <Button
                        type="secondary"
                        text="Contact Seller"
                        width="fluid"
                        disabled={product.out_of_stock}
                        onClick={() => {
                          openChatModalWithRecipientAndProductId({
                            recipientUid: seller?.uid ?? '',
                            productId: product.id,
                          });
                        }}
                        className="!h-[5.5rem] !text-[1.8rem]"
                      />

                      <Button
                        type="outline"
                        width="fluid"
                        text={liked ? 'In Favorites' : 'Add to Favorites'}
                        leadingIcon={
                          <HeartIcon
                            className={`${
                              liked ? 'fill-white' : '!text-brand-secondary'
                            } h-10 w-10`}
                          />
                        }
                        className={`${
                          liked
                            ? '!border-none !bg-brand-secondary text-white'
                            : 'border-brand-secondary text-brand-secondary'
                        }  !h-[5.5rem] text-[1.8rem]`}
                        disabled={product.out_of_stock}
                        onClick={handleLikeClick}
                      />
                    </div>

                    <div className="flex w-full justify-end">
                      <Button
                        text="Report this item"
                        type="text"
                        width="small"
                        className="!text-[1.6rem] !font-semibold"
                        onClick={() => {
                          setOpenModal('reportItem');
                        }}
                        leadingIcon={
                          <FlagIcon
                            fill="white"
                            stroke="#C41719"
                            strokeWidth={2}
                            width={14}
                            height={14}
                          />
                        }
                      />

                      <ReportItemModal
                        isOpen={openModal === 'reportItem'}
                        itemId={product.id}
                        isUser={false}
                        dismiss={() => setOpenModal('')}
                      />
                    </div>
                  </div>

                  {userDoc?.roles?.admin && (
                    <>
                      <Button
                        text="Estimate Shipping"
                        onClick={() => setOpenModal('estimateShipping')}
                      />

                      <EstimateDirtBikeShippingModal
                        isOpen={openModal === 'estimateShipping'}
                        dismiss={() => setOpenModal('')}
                        product_id={product.id}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 sm:mt-16">
            <BikeDetails product={product} />
          </div>
        </div>

        <div className="flex w-full flex-col gap-8 sm:mx-auto sm:max-w-default">
          {!!relatedProducts?.results?.length && (
            <HomeSection
              title="Similar Listings"
              className="mt-32 px-4 sm:px-0"
              carouselWrapper
            >
              <ResponsiveCarousel
                basis="basis-[48%] sm:basis-1/4"
                desktopIncrementBy={4}
                mobileIncrementBy={2}
                items={
                  relatedProducts?.results?.map((product) => (
                    <MXProductCard
                      key={product.id}
                      product={product}
                      attribution={{
                        data_source: 'typesense',
                        page: 'dirt-bikes-for-sale',
                        section: 'similar-listings',
                      }}
                    />
                  )) ?? []
                }
              />
            </HomeSection>
          )}

          <PriceGuideChart product={product} />
        </div>

        {seller && (
          <div className="mt-16 w-full bg-[#F6F6F6] pb-8 pt-16">
            <div className="flex w-full sm:mx-auto sm:max-w-default">
              <ProductMeetTheSeller
                sellerDoc={seller}
                reviews={reviews ?? []}
                product_id={product.id}
                isLoadingReviews={isLoadingReviews}
              />
            </div>
          </div>
        )}

        {!!moreProductsFromSeller?.length && (
          <div className={`w-full ${montserrat.className}`}>
            <HomeSection
              title="More from this seller"
              titleClassName="!text-[2.5rem]"
              className="mt-32 px-4 sm:px-0"
              fullWidth
              backgroundColor="bg-[#F6F6F6]"
              carouselWrapper
            >
              <Carousel
                basis="basis-[48%] sm:basis-auto"
                items={moreProductsFromSeller.map((product) => (
                  <MXProductCard
                    key={product.id}
                    product={product}
                    attribution={{
                      data_source: 'typesense',
                      section: 'more-from-seller',
                    }}
                  />
                ))}
              />
            </HomeSection>
          </div>
        )}

        <div className="flex w-full flex-col gap-8 sm:mx-auto sm:max-w-default">
          {!!parts.length && (
            <HomeSection
              title="Parts for this bike"
              className="mt-32 px-4 sm:px-0"
              carouselWrapper
            >
              <ResponsiveCarousel
                basis="basis-[48%] sm:basis-1/4"
                desktopIncrementBy={4}
                mobileIncrementBy={2}
                items={
                  parts?.map((product) => (
                    <MXProductCard
                      key={product.id}
                      product={product}
                      attribution={{
                        data_source: 'typesense',
                        page: 'dirt-bikes-for-sale',
                        section: 'parts-for-this-bike',
                      }}
                    />
                  )) ?? []
                }
              />
            </HomeSection>
          )}
        </div>
      </div>

      {imageViewerOpen && (
        <ProductViewerModal
          isOpen={imageViewerOpen}
          dismiss={() => setImageViewerOpen(false)}
          images={product.images}
          video={product.video}
          initialImage={currentImageIndex}
        />
      )}
    </>
  );
}
